import type { Prisma } from "@gt/prisma";
import { getClusterSignupHomePath } from "@/pages/my-cluster/myCluster.const";

export type UserWithCompany = Prisma.UserGetPayload<{ include: { company: true } }> & {
  signupSource?: "MAIN" | "CLUSTER" | null;
  signupClusterSlug?: string | null;
  signupCluster?: { headUserId: string; industry: string } | null;
};

type CompanyWithOptionalAdminInvite = UserWithCompany["company"] & {
  adminCreatedInvite?: { status: string } | null;
};

/** Company statuses that may browse, shortlist, and complete the intent questionnaire. */
export const EXPLORE_OPPORTUNITIES_STATUSES = ["SYSTEM_VERIFIED", "UNDER_REVIEW", "VERIFIED"] as const;

export {
  canExploreOpportunities,
  canSendInterestRequest,
} from "@shared/companyVerificationGates";

/** Company has passed external/system verification — full app routes allowed. */
export const CLIENT_ONBOARDING_COMPLETE_STATUSES = new Set(["SYSTEM_VERIFIED", "VERIFIED", "UNDER_REVIEW"]);

/** Statuses that mean the user has finished profile builder at least once (not signup-only company). */
export const CLIENT_PROFILE_CREATED_STATUSES = new Set([
  "UNDER_REVIEW",
  "SYSTEM_VERIFIED",
  "VERIFIED",
  "ACTION_REQUIRED",
]);

export function isClientOnboardingComplete(user: UserWithCompany | null | undefined): boolean {
  const status = user?.company?.status;
  return Boolean(user?.company && status && CLIENT_ONBOARDING_COMPLETE_STATUSES.has(status));
}

/**
 * True once the client has saved a real company profile (not just signup shell).
 * Derived only from API user/company payload — no localStorage.
 */
export function hasClientCreatedProfile(user: UserWithCompany | null | undefined): boolean {
  const company = user?.company;
  if (!company) return false;

  if (company.website?.trim()) return true;
  if (company.description?.trim()) return true;
  if (company.status && CLIENT_PROFILE_CREATED_STATUSES.has(company.status)) return true;

  return false;
}

/**
 * True when registry verification CTAs / modal should show (GST, CIN, etc.).
 * Claim and self-serve profile completion keep DRAFT until registry verification is submitted.
 */
export function shouldPromptCompanyRegistryVerification(
  user: UserWithCompany | null | undefined
): boolean {
  const company = user?.company;
  if (!company || !hasClientCreatedProfile(user)) return false;

  const status = company.status;
  if (!status || status === "VERIFIED" || status === "SYSTEM_VERIFIED") return false;

  if (status === "UNDER_REVIEW") {
    const inviteStatus = (company as CompanyWithOptionalAdminInvite).adminCreatedInvite?.status;
    // Admin-invited companies still need registry verification after claim.
    if (inviteStatus === "CLAIMED") return true;
    // Self-serve users already submitted — wait for admin review without re-prompting.
    return false;
  }

  return true;
}

export function isFlexDemoAssigned(user: UserWithCompany | null | undefined): boolean {
  const company = user?.company;
  if (!company) return false;
  return Boolean(company.flexDemoRequired && !company.flexDemoCompletedAt);
}

/** GST / registry details have been submitted (modal no longer required). */
export function hasSubmittedRegistryVerification(user: UserWithCompany | null | undefined): boolean {
  return hasClientCreatedProfile(user) && !shouldPromptCompanyRegistryVerification(user);
}

/** Company intent 15-question questionnaire has been submitted. */
export function hasCompletedCompanyIntent(user: UserWithCompany | null | undefined): boolean {
  const completedAt = (
    user?.company as { intentQuestionnaire?: { completedAt?: Date | string | null } | null } | null | undefined
  )?.intentQuestionnaire?.completedAt;
  return Boolean(completedAt);
}

/** Hard-lock into the sandbox demo only after GST submit and the 15-question intent form. */
export function isClusterSignupUser(user: UserWithCompany | null | undefined): boolean {
  return user?.signupSource === "CLUSTER";
}

export function needsFlexDemo(user: UserWithCompany | null | undefined): boolean {
  if (isClusterSignupUser(user)) return false;
  return (
    isFlexDemoAssigned(user) &&
    hasSubmittedRegistryVerification(user) &&
    hasCompletedCompanyIntent(user)
  );
}

/** Home after profile exists (create-profile complete, skip the empty-profile gate). */
export function getClientHomePathAfterProfile(user: UserWithCompany | null | undefined): string {
  if (needsFlexDemo(user)) return "/flex-demo";
  const clusterHome = getClusterSignupHomePath(user);
  if (clusterHome) return clusterHome;
  return "/my-discovery";
}

/** Where to send a CLIENT after auth when using default app shell (not admin). */
export function getDefaultClientPathAfterAuth(user: UserWithCompany | null | undefined): string {
  // API failure / unknown user: never assume "new user" — avoid trapping existing clients on create-profile.
  if (!user) return "/my-discovery";
  if (!hasClientCreatedProfile(user)) return "/create-profile";
  return getClientHomePathAfterProfile(user);
}

/** Post-login destination from portal user role (clients, facilitators, admins). */
export function getDefaultPathAfterAuth(user: UserWithCompany | null | undefined): string {
  if (!user) return "/my-discovery";
  if (user.role === "FACILITATOR") return "/facilitator/dashboard";
  if (user.role === "ECOSYSTEM_HEAD") return "/ecosystem-head/dashboard";
  if (user.role === "EXPERT") return "/expert/dashboard";
  if (user.role === "MANAGER") return getDefaultClientPathAfterAuth(user);
  if (user.role === "ADMIN" || user.role === "SUB_ADMIN") return "/admin/companies";
  return getDefaultClientPathAfterAuth(user);
}
